import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  LinearProgress,
  Pagination,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Snackbar,
  Link,
} from '@mui/material';
import {
  NetworkCheck,
  Refresh,
  FilterList,
  Visibility,
  TrendingUp,
  TrendingDown,
  Schedule,
  DataUsage,
  Speed,
  DeviceHub,
} from '@mui/icons-material';
import { Connection, ConnectionStats, ConnectionService } from '../../services/connectionService';
import { ContratoServicos, ContratoServicosService } from '../../services/contratoServicosService';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import { formatarDataHoraSegundos, formatarDuracaoTotal } from '../../utils/formatToView';

interface ContractConnectionsProps {
  contractId: number;
}

const ContractConnections: React.FC<ContractConnectionsProps> = ({ contractId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [connections, setConnections] = useState<Connection[]>([]);
  const [stats, setStats] = useState<ConnectionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalConnections, setTotalConnections] = useState(0);
  
  // Filtros
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [services, setServices] = useState<ContratoServicos[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('all');
  
  
  // Toast para validações
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Modais
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    loadConnections();
  }, [contractId, page, showActiveOnly, selectedServiceId]);

  useEffect(() => {
    loadStats();
  }, [contractId]);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const resp = await ContratoServicosService.getByContractId(contractId);
        if (resp?.success && Array.isArray(resp.data)) {
          setServices(resp.data);
        } else {
          setServices([]);
        }
      } catch (err) {
        setServices([]);
      }
    };
    loadServices();
  }, [contractId]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await ConnectionService.getByContract(contractId, {
        page,
        limit: 20,
        onlyActive: showActiveOnly,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        serviceId: selectedServiceId !== 'all' ? Number(selectedServiceId) : undefined,
      });

      if (response.success) {
        setConnections(response.data);
        setTotalPages(response.pagination.pages);
        setTotalConnections(response.pagination.total);
        setStats(response.stats);
      } else {
        setError('Erro ao carregar conexões');
      }
    } catch (err: any) {
      setError(`Erro ao carregar conexões: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await ConnectionService.getStatsByContract(contractId, 30);
      if (response.success) {
        setStats(response.data);
      }
    } catch (err: any) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleViewDetails = (connection: Connection) => {
    setSelectedConnection(connection);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedConnection(null);
  };

  const applyFilters = () => {
    // Validação: data de início não pode ser maior que data fim
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (start > end) {
        setToastMessage('Data de início não pode ser maior que data fim');
        setToastOpen(true);
        return;
      }
    }
    
    setPage(1); // Reset to first page when applying filters
    loadConnections();
  };

  const clearFilters = () => {
    setShowActiveOnly(false);
    setStartDate('');
    setEndDate('');
    setSelectedServiceId('all');
    setPage(1);
    setError(null); // Limpar erro ao limpar filtros
    // Automatically load connections after clearing filters
    setTimeout(() => {
      loadConnections();
    }, 100);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  // Função auxiliar para formatar bytes com tratamento de erro
  const safeFormatBytes = (bytes: number | undefined | null): string => {
    try {
      if (!bytes || bytes === 0) return '0 B';
      return ConnectionService.formatBytes(bytes);
    } catch (error) {
      console.error('Erro ao formatar bytes:', error, bytes);
      return 'N/A';
    }
  };

  // Função auxiliar para formatar duração com tratamento de erro
  const safeFormatDuration = (seconds: number | undefined | null): string => {
    try {
      if (!seconds || seconds === 0) return '0s';
      return formatarDuracaoTotal(seconds);
    } catch (error) {
      console.error('Erro ao formatar duração:', error, seconds);
      return 'N/A';
    }
  };

  // Função auxiliar para obter status com tratamento de erro
  const safeGetStatusLabel = (connection: Connection): string => {
    try {
      return ConnectionService.getStatusLabel(connection);
    } catch (error) {
      console.error('Erro ao obter status:', error, connection);
      return 'Desconhecido';
    }
  };

  // Função auxiliar para obter cor do status com tratamento de erro
  const safeGetStatusColor = (connection: Connection): 'success' | 'error' | 'default' => {
    try {
      return ConnectionService.getStatusColor(connection);
    } catch (error) {
      console.error('Erro ao obter cor do status:', error, connection);
      return 'default';
    }
  };

  if (loading && connections.length === 0) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando registros de conexões...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6" fontWeight="bold">
          Registros de Conexões
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Atualizar">
            <IconButton onClick={loadConnections} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Estatísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ m: 0, p: 0 }} >
              <CardContent style={{ padding: 0 }} sx={{ textAlign: 'center', py: 0.2, my: 0, mb: 0, px: 0.5 }}>
                <Schedule color="primary" sx={{ fontSize: 32, mb: 0, mt: 0 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ m: 0, p: 0 }}>
                  {stats.totalSessions}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ m: 0, p: 0 }}>
                  Total de Sessões
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ m: 0, p: 0 }}>
              <CardContent style={{ padding: 0 }} sx={{ textAlign: 'center', py: 0.2, my: 0, mb: 0, px: 0.5 }}>
                <Schedule color="info" sx={{ fontSize: 32, mb: 0, mt: 0 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ m: 0, p: 0 }}>
                  {safeFormatDuration(stats.totalTime)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ m: 0, p: 0 }}>
                  Tempo Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ m: 0, p: 0 }}>
              <CardContent style={{ padding: 0 }} sx={{ textAlign: 'center', py: 0.2, my: 0, mb: 0, px: 0.5 }}>
                <TrendingUp color="success" sx={{ fontSize: 32, mb: 0, mt: 0 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ m: 0, p: 0 }}>
                  {safeFormatBytes(stats.totalDownload)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ m: 0, p: 0 }}>
                  Download Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ m: 0, p: 0 }}>
              <CardContent style={{ padding: 0 }} sx={{ textAlign: 'center', py: 0.2, my: 0, mb: 0, px: 0.5 }}>
                <TrendingDown color="warning" sx={{ fontSize: 32, mb: 0, mt: 0 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ m: 0, p: 0 }}>
                  {safeFormatBytes(stats.totalUpload)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ m: 0, p: 0 }}>
                  Upload Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Serviço</InputLabel>
              <Select
                value={selectedServiceId}
                onChange={(e) => {
                  setSelectedServiceId(String(e.target.value));
                }}
                label="Serviço"
                size="small"
              >
                <MenuItem value="all">Todos os Serviços</MenuItem>
                {services
                  .filter((s) => s.tipo === 'internet')
                  .map((s) => {
                  const parts: string[] = [];
                  if (s.tipo) parts.push(String(s.tipo));
                  if (s.plano?.nome) parts.push(s.plano.nome);
                  if (s.usuario_pppoe) parts.push(`(${s.usuario_pppoe})`);
                  const label = parts.join(' - ');
                  return (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {label || `Serviço ${s.id}`}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={showActiveOnly ? 'active' : 'all'}
                  onChange={(e) => setShowActiveOnly(e.target.value === 'active')}
                  label="Status"
                  size="small"
                >
                  <MenuItem value="all">Todas as conexões</MenuItem>
                  <MenuItem value="active">Apenas ativas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          <Grid item xs={12} sm={3}>
              <SysDateRangePicker
                onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                  const toYmd = (d: Date | null) => d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '';
                  setStartDate(toYmd(range.startDate));
                  setEndDate(toYmd(range.endDate));
                }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  Aplicar
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  Limpar
                </Button>
              </Box>
            </Grid>
        </Grid>
      </Box>

      {/* Tabela de Conexões */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Conexões ({totalConnections})
            </Typography>
            {loading && <LinearProgress sx={{ width: 100 }} />}
          </Box>

          {connections.length === 0 ? (
            <Box textAlign="center" py={4}>
              <NetworkCheck sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhuma conexão encontrada
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {showActiveOnly || startDate || endDate
                  ? 'Nenhuma conexão encontrada com os filtros aplicados'
                  : 'Este contrato ainda não possui registros de conexão'
                }
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuário</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Início</TableCell>
                      <TableCell>Fim</TableCell>
                      <TableCell>Duração</TableCell>
                      <TableCell>IP</TableCell>
                      {!isMobile && <TableCell>Download</TableCell>}
                      {!isMobile && <TableCell>Upload</TableCell>}
                      <TableCell>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {connections.map((connection) => (
                      <TableRow key={connection.radacctid}>
                        <TableCell>
                          {connection.username || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ConnectionService.getStatusLabel(connection)}
                            color={ConnectionService.getStatusColor(connection)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {connection.acctstarttime 
                            ? formatarDataHoraSegundos(connection.acctstarttime)
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          {connection.acctstoptime 
                            ? formatarDataHoraSegundos(connection.acctstoptime)
                            : 'Em andamento'
                          }
                        </TableCell>
                        <TableCell>
                          {connection.acctsessiontime 
                            ? formatarDuracaoTotal(connection.acctsessiontime)
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          <Link href={`http://${connection.framedipaddress}`} target="_blank">
                            {connection.framedipaddress || 'N/A'}
                          </Link>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            {connection.acctoutputoctets 
                              ? ConnectionService.formatBytes(connection.acctoutputoctets)
                              : 'N/A'
                            }
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            {connection.acctinputoctets 
                              ? ConnectionService.formatBytes(connection.acctinputoctets)
                              : 'N/A'
                            }
                          </TableCell>
                        )}
                        <TableCell>
                          <Tooltip title="Ver detalhes">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(connection)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginação */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Detalhes da Conexão
        </DialogTitle>
        <DialogContent>
          {selectedConnection && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  ID da Sessão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctsessionid}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={ConnectionService.getStatusLabel(selectedConnection)}
                  color={ConnectionService.getStatusColor(selectedConnection)}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Usuário
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.username || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  IP do Cliente
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <Link href={`http://${selectedConnection.framedipaddress}`} target="_blank">
                    {selectedConnection.framedipaddress || 'N/A'}
                  </Link>
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  NAS IP
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.nasipaddress}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  MAC Address
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.callingstationid || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  PPPoE Server
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.calledstationid || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Interface
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.nasportid || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Início da Sessão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctstarttime 
                    ? formatarDataHoraSegundos(selectedConnection.acctstarttime)
                    : 'N/A'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Fim da Sessão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctstoptime 
                    ? formatarDataHoraSegundos(selectedConnection.acctstoptime)
                    : 'Em andamento'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Duração Total
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctsessiontime 
                    ? formatarDuracaoTotal(selectedConnection.acctsessiontime)
                    : 'N/A'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Motivo da Desconexão
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctterminatecause 
                    ? ConnectionService.getTerminateCauseLabel(selectedConnection.acctterminatecause)
                    : 'N/A'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Dados Enviados (Upload)
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctinputoctets 
                    ? ConnectionService.formatBytes(selectedConnection.acctinputoctets)
                    : 'N/A'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Dados Recebidos (Download)
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.acctoutputoctets 
                    ? ConnectionService.formatBytes(selectedConnection.acctoutputoctets)
                    : 'N/A'
                  }
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Protocolo
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.framedprotocol || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Tipo de Serviço
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedConnection.servicetype || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast para validações */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity="error" sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractConnections; 